import React, { useState, useEffect } from "react";
import "./view-transaction.css";
import OffCanvas from "../../../components/OffCanvas/OffCanvas.tsx";
import {
  reSend,
  ticketUpgradeVerify,
  ticketUpgradeConfirm,
  ticketUpgradeResendConfirmation,
  ticketCancellationPreview,
  ticketCancellationConfirm,
} from "../../../_services/dashboard.js";
import { environment } from "../../../environment/enviroment.js";
import toast, { Toaster } from "react-hot-toast";
import { IoRefresh } from "react-icons/io5";
import { IoIosClose } from "react-icons/io";

const TICKET_COLORS = [
  "#b45309",
  "#0369a1",
  "#7c3aed",
  "#047857",
  "#ca8a04",
  "#0891b2",
  "#6d28d9",
  "#65a30d",
];

function jsonToUrlParams(json: Record<string, unknown>) {
  const params = new URLSearchParams();
  for (const key in json) {
    if (Object.prototype.hasOwnProperty.call(json, key) && json[key] !== undefined) {
      params.append(key, String(json[key]));
    }
  }
  return params.toString();
}

function getApiErrorMessage(error: unknown, fallback: string) {
  if (error && typeof error === "object" && "response" in error) {
    const response = (
      error as {
        response?: {
          data?: {
            message?: string;
            data?: Record<string, string[] | string>;
          };
        };
      }
    ).response;

    const fieldErrors = response?.data?.data;
    if (fieldErrors && typeof fieldErrors === "object") {
      const details = Object.entries(fieldErrors)
        .map(([field, messages]) => {
          const text = Array.isArray(messages) ? messages.join(", ") : String(messages);
          return `${field}: ${text}`;
        })
        .filter(Boolean);
      if (details.length > 0) {
        const base = response?.data?.message || "Invalid request data";
        return `${base} — ${details.join("; ")}`;
      }
    }

    if (response?.data?.message) return response.data.message;
  }
  return fallback;
}

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="detail-row">
      <span className="detail-row__label">{label}</span>
      <span className="detail-row__value">{value}</span>
    </div>
  );
}

interface EventTicket {
  id: number;
  ticket_name: string;
  ticket_amount?: string;
}

function getParticipantTicketAmount(
  participantTicket: any,
  eventTickets: EventTicket[]
): number {
  if (participantTicket?.ticket_amount != null) {
    return Number(participantTicket.ticket_amount);
  }
  const match = eventTickets.find(
    (t) =>
      t.id === participantTicket?.ticket_id ||
      t.ticket_name === participantTicket?.ticket_name
  );
  return Number(match?.ticket_amount) || 0;
}

function getUpgradeableEventTickets(
  participantTicket: any,
  eventTickets: EventTicket[]
): EventTicket[] {
  const currentAmount = getParticipantTicketAmount(participantTicket, eventTickets);
  return eventTickets.filter((t) => Number(t.ticket_amount) > currentAmount);
}

function canUpgradeTicket(participantTicket: any, eventTickets: EventTicket[]): boolean {
  const hasNoSeats = !participantTicket?.seats || participantTicket.seats.length === 0;
  if (!hasNoSeats) return false;
  return getUpgradeableEventTickets(participantTicket, eventTickets).length > 0;
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

/** Ticket purchased with a coupon code — refund should not be offered. */
function isCouponRedeemedTicket(_ticket: any, viewData: any): boolean {
  if (String(viewData?.provider || "").toUpperCase() === "COUPON") return true;
  if (viewData?.coupon_code || viewData?.coupon) return true;
  return false;
}

/** Koko / MintPay payments cannot be refunded via cancel+refund. */
function isKokoOrMintPayTicket(_ticket: any, viewData: any): boolean {
  const provider = String(viewData?.provider || "")
    .toUpperCase()
    .replace(/[\s_-]/g, "");

  return (
    provider === "KOKO" ||
    provider === "KOKOPAY" ||
    provider === "MINTPAY" ||
    provider === "MINT"
  );
}

/** No refundable amount (100% discount / free ticket). */
function isFullyDiscountedOrFreeTicket(ticket: any, viewData: any): boolean {
  const discountPercentage =
    toNumber(ticket?.discount_percentage) ??
    toNumber(ticket?.coupon_discount_percentage) ??
    toNumber(viewData?.discount_percentage) ??
    toNumber(viewData?.coupon_discount_percentage);

  if (discountPercentage !== null && discountPercentage >= 100) return true;

  if (
    ticket?.is_free_ticket === true ||
    ticket?.is_free_ticket === 1 ||
    ticket?.is_free === true ||
    ticket?.is_coupon_100 === true ||
    viewData?.is_free_ticket === true ||
    viewData?.is_free_ticket === 1
  ) {
    return true;
  }

  const ticketAmount = toNumber(ticket?.ticket_amount);
  const ticketPaid =
    toNumber(ticket?.paid_amount) ??
    toNumber(ticket?.amount_paid) ??
    toNumber(ticket?.net_amount) ??
    toNumber(ticket?.ticket_paid_amount);

  if (ticketPaid !== null && ticketPaid <= 0) return true;
  if (ticketAmount !== null && ticketAmount <= 0) return true;

  const discountAmount =
    toNumber(ticket?.discount_amount) ??
    toNumber(ticket?.coupon_discount) ??
    toNumber(viewData?.discount_amount) ??
    toNumber(viewData?.coupon_discount);

  if (
    ticketAmount !== null &&
    discountAmount !== null &&
    discountAmount > 0 &&
    discountAmount >= ticketAmount
  ) {
    return true;
  }

  // Whole transaction paid nothing (100% coupon order)
  const transactionPaid =
    toNumber(viewData?.paid_amount) ??
    toNumber(viewData?.amount_paid) ??
    toNumber(viewData?.total_amount) ??
    toNumber(viewData?.net_amount) ??
    toNumber(viewData?.total_paid);

  if (transactionPaid !== null && transactionPaid <= 0) return true;

  return false;
}

function isRefundUnavailable(ticket: any, viewData: any): boolean {
  return (
    isCouponRedeemedTicket(ticket, viewData) ||
    isKokoOrMintPayTicket(ticket, viewData) ||
    isFullyDiscountedOrFreeTicket(ticket, viewData)
  );
}

const REFUND_UNAVAILABLE_MESSAGE =
  "Cancel + Refund is not available for coupon, free, Koko, or MintPay tickets. Use Cancel Only.";

/*
function formatVerifyLabel(key: string) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function renderVerifyValue(value: unknown) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function VerifyDetailsBlock({ details }: { details: Record<string, unknown> }) {
  return (
    <div className="upgrade-modal__verify-details">
      <h4 className="upgrade-modal__verify-title">Verification Details</h4>
      {Object.entries(details)
        .filter(([key]) => key !== "status" && key !== "message")
        .map(([key, value]) => (
          <div key={key} className="upgrade-modal__verify-row">
            <span className="upgrade-modal__verify-label">{formatVerifyLabel(key)}</span>
            <span className="upgrade-modal__verify-value">{renderVerifyValue(value)}</span>
          </div>
        ))}
    </div>
  );
}
*/

const REFUND_REASONS = [
  { value: "REQUESTED_BY_CUSTOMER", label: "Requested by customer" },
];

type CancellationMode = "cancel_only" | "cancel_and_refund";

interface CancellationModalProps {
  isOpen: boolean;
  ticket: any;
  viewData: any;
  onClose: () => void;
  onSuccess: () => void;
  getTicketId: (ticket: any) => number | undefined;
}

function TicketCancellationModal({
  isOpen,
  ticket,
  viewData,
  onClose,
  onSuccess,
  getTicketId,
}: CancellationModalProps) {
  const [cancelCount, setCancelCount] = useState(1);
  const [cancellationMode, setCancellationMode] = useState<CancellationMode>("cancel_only");
  const [refundReason, setRefundReason] = useState("REQUESTED_BY_CUSTOMER");
  const [cancellationNote, setCancellationNote] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState("");

  const maxCount = Math.floor(Number(ticket?.count)) || 1;
  const refundUnavailable = isRefundUnavailable(ticket, viewData);

  const resetVerification = () => {
    setIsVerified(false);
    setVerifyMessage("");
  };

  useEffect(() => {
    if (isOpen && ticket) {
      setCancelCount(maxCount);
      setCancellationMode("cancel_only");
      setRefundReason("REQUESTED_BY_CUSTOMER");
      setCancellationNote("");
      setIsVerified(false);
      setVerifyMessage("");
    }
  }, [isOpen, ticket, maxCount]);

  useEffect(() => {
    if (refundUnavailable && cancellationMode === "cancel_and_refund") {
      setCancellationMode("cancel_only");
      setIsVerified(false);
      setVerifyMessage("");
    }
  }, [refundUnavailable, cancellationMode]);

  if (!isOpen || !ticket) return null;

  const ticketId = getTicketId(ticket);

  const buildQueryParams = () =>
    jsonToUrlParams({
      onepay_transaction_id: viewData.onepay_transaction_id,
      customer_email: viewData.customer_email,
    });

  const buildBody = () => {
    const body: Record<string, unknown> = {
      ticket_id: ticketId,
      cancel_count: Number(cancelCount) || 1,
      cancellation_mode: cancellationMode,
      cancellation_note: cancellationNote.trim(),
    };
    if (cancellationMode === "cancel_and_refund") {
      body.refund_reason = refundReason;
    }
    return body;
  };

  const handleClose = () => {
    onClose();
  };

  const handleVerify = async () => {
    if (!viewData?.onepay_transaction_id || !viewData?.customer_email) {
      toast.error("Transaction details are missing.");
      return;
    }
    if (!ticketId) {
      toast.error("Could not find the ticket ID.");
      return;
    }
    if (!cancelCount || cancelCount < 1) {
      toast.error("Please enter a valid cancel count.");
      return;
    }
    if (!cancellationNote.trim()) {
      toast.error("Please enter a cancellation note.");
      return;
    }

    setIsVerifying(true);
    resetVerification();

    try {
      const response = await ticketCancellationPreview(buildQueryParams(), buildBody());

      if (response?.data?.status === 200) {
        setVerifyMessage(response?.data?.message || "Preview loaded successfully!");
        setIsVerified(true);
      } else {
        toast.error(response?.data?.message || "Cancellation preview failed.");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "An error occurred during cancellation preview."));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleConfirm = async () => {
    if (!isVerified) return;

    setIsConfirming(true);
    try {
      const response = await ticketCancellationConfirm(buildQueryParams(), buildBody());

      if (response?.data?.status === 200) {
        toast.success(response?.data?.message || "Ticket cancelled successfully!");
        handleClose();
        onSuccess();
      } else {
        toast.error(response?.data?.message || "Cancellation confirmation failed.");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "An error occurred during cancellation confirmation."));
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="upgrade-modal-overlay" onClick={handleClose}>
      <div
        className="upgrade-modal upgrade-modal--wide"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-modal-title"
      >
        <div className="upgrade-modal__header">
          <h3 id="cancel-modal-title" className="upgrade-modal__title">
            Ticket Cancellation
          </h3>
          <button type="button" className="upgrade-modal__close" aria-label="Close" onClick={handleClose}>
            <IoIosClose />
          </button>
        </div>

        <div className="upgrade-modal__body">
          {refundUnavailable && (
            <div className="cancel-modal__blocked-banner">
              {REFUND_UNAVAILABLE_MESSAGE}
            </div>
          )}
          <div className="cancel-modal__fee-banner">
            Rs. 100 handling fee per ticket applies to all cancellations.
          </div>

          <div className="upgrade-modal__field">
            <label className="upgrade-modal__label">Current Ticket</label>
            <div className="upgrade-modal__readonly">{ticket.ticket_name}</div>
          </div>

          <div className="upgrade-modal__field">
            <label className="upgrade-modal__label" htmlFor="cancel-count">
              Number of Tickets to Cancel
            </label>
            <input
              id="cancel-count"
              type="number"
              className="upgrade-modal__input"
              min={1}
              max={maxCount}
              value={cancelCount}
              onChange={(e) => {
                setCancelCount(Number(e.target.value));
                resetVerification();
              }}
              disabled={isVerifying || isConfirming}
            />
            <span className="upgrade-modal__hint">Max available: {maxCount}</span>
          </div>

          <div className="upgrade-modal__field">
            <span className="upgrade-modal__label">Cancellation Type</span>
            <div className="cancel-modal__modes">
              <button
                type="button"
                className={`cancel-modal__mode${cancellationMode === "cancel_only" ? " cancel-modal__mode--active" : ""}`}
                onClick={() => {
                  setCancellationMode("cancel_only");
                  resetVerification();
                }}
                disabled={isVerifying || isConfirming}
              >
                <strong>Cancel Only</strong>
                <span>No refund processed. Rs. 100 ticket fee still applies.</span>
              </button>
              {!refundUnavailable && (
                <button
                  type="button"
                  className={`cancel-modal__mode cancel-modal__mode--refund${cancellationMode === "cancel_and_refund" ? " cancel-modal__mode--active" : ""}`}
                  onClick={() => {
                    setCancellationMode("cancel_and_refund");
                    resetVerification();
                  }}
                  disabled={isVerifying || isConfirming}
                >
                  <strong>Cancel + Refund</strong>
                  <span>Refund = ticket price − Rs. 100 handling fee per ticket.</span>
                </button>
              )}
            </div>
          </div>

          {cancellationMode === "cancel_and_refund" && (
            <div className="upgrade-modal__field">
              <label className="upgrade-modal__label" htmlFor="refund-reason">
                Refund Reason
              </label>
              <select
                id="refund-reason"
                className="upgrade-modal__select"
                value={refundReason}
                onChange={(e) => {
                  setRefundReason(e.target.value);
                  resetVerification();
                }}
                disabled={isVerifying || isConfirming}
              >
                {REFUND_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="upgrade-modal__field">
            <label className="upgrade-modal__label" htmlFor="cancel-note">
              Cancellation Note
            </label>
            <textarea
              id="cancel-note"
              className="upgrade-modal__textarea"
              rows={3}
              placeholder="Enter reason for cancellation..."
              value={cancellationNote}
              onChange={(e) => {
                setCancellationNote(e.target.value);
                resetVerification();
              }}
              disabled={isVerifying || isConfirming}
            />
          </div>

          {/* {verifyDetails && <VerifyDetailsBlock details={verifyDetails} />} */}
          {isVerified && verifyMessage && (
            <div className="upgrade-modal__verified-msg">{verifyMessage}</div>
          )}
        </div>

        <div className="upgrade-modal__footer">
          <button
            type="button"
            className="upgrade-modal__btn upgrade-modal__btn--cancel"
            onClick={handleClose}
            disabled={isVerifying || isConfirming}
          >
            Close
          </button>
          {!isVerified ? (
            <button
              type="button"
              className="upgrade-modal__btn upgrade-modal__btn--verify"
              onClick={handleVerify}
              disabled={isVerifying}
            >
              {isVerifying ? "Loading preview..." : "Preview"}
            </button>
          ) : (
            <button
              type="button"
              className="upgrade-modal__btn upgrade-modal__btn--confirm"
              onClick={handleConfirm}
              disabled={isConfirming}
            >
              {isConfirming ? "Confirming..." : "Confirm Cancellation"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface UpgradeModalProps {
  isOpen: boolean;
  ticket: any;
  eventTickets: EventTicket[];
  viewData: any;
  onClose: () => void;
  onSuccess: () => void;
  getCurrentTicketId: (ticket: any) => number | undefined;
}

function TicketUpgradeModal({
  isOpen,
  ticket,
  eventTickets,
  viewData,
  onClose,
  onSuccess,
  getCurrentTicketId,
}: UpgradeModalProps) {
  const [upgradeTicketId, setUpgradeTicketId] = useState("");
  const [upgradeCount, setUpgradeCount] = useState(1);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState("");

  const maxCount = Math.floor(Number(ticket?.count)) || 1;

  useEffect(() => {
    if (isOpen && ticket) {
      setUpgradeTicketId("");
      setUpgradeCount(maxCount);
      setIsVerified(false);
      setVerifyMessage("");
    }
  }, [isOpen, ticket, maxCount]);

  if (!isOpen || !ticket) return null;

  const currentTicketId = getCurrentTicketId(ticket);
  const currentTicketName = ticket.ticket_name;
  const upgradeOptions = getUpgradeableEventTickets(ticket, eventTickets);

  const buildQueryParams = () =>
    jsonToUrlParams({
      onepay_transaction_id: viewData.onepay_transaction_id,
      customer_email: viewData.customer_email,
    });

  const buildBody = () => ({
    current_ticket_id: currentTicketId,
    upgrade_ticket_id: Number(upgradeTicketId),
    upgrade_ticket_count: Number(upgradeCount) || 1,
    organizer_email: environment.organizerEmail,
  });

  const handleClose = () => {
    setUpgradeTicketId("");
    setUpgradeCount(maxCount);
    setIsVerified(false);
    setVerifyMessage("");
    onClose();
  };

  const handleVerify = async () => {
    if (!viewData?.onepay_transaction_id || !viewData?.customer_email) {
      toast.error("Transaction details are missing.");
      return;
    }
    if (!currentTicketId) {
      toast.error("Could not find the current ticket ID.");
      return;
    }
    if (!upgradeTicketId) {
      toast.error("Please select an upgrade ticket.");
      return;
    }
    if (!upgradeCount || upgradeCount < 1) {
      toast.error("Please enter a valid ticket count.");
      return;
    }

    setIsVerifying(true);
    setIsVerified(false);
    setVerifyMessage("");

    try {
      const response = await ticketUpgradeVerify(buildQueryParams(), buildBody());

      if (response?.data?.status === 200) {
        setVerifyMessage(response?.data?.message || "Verification successful!");
        setIsVerified(true);
        toast.success(response?.data?.message || "Verification successful!");
      } else {
        toast.error(response?.data?.message || "Verification failed.");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "An error occurred during verification."));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleConfirm = async () => {
    if (!isVerified) return;

    setIsConfirming(true);
    try {
      const response = await ticketUpgradeConfirm(buildQueryParams(), buildBody());

      if (response?.data?.status === 200) {
        toast.success(response?.data?.message || "Ticket upgraded successfully!");
        handleClose();
        onSuccess();
      } else {
        toast.error(response?.data?.message || "Ticket upgrade confirmation failed.");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "An error occurred during confirmation."));
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="upgrade-modal-overlay" onClick={handleClose}>
      <div
        className="upgrade-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-modal-title"
      >
        <div className="upgrade-modal__header">
          <h3 id="upgrade-modal-title" className="upgrade-modal__title">
            Ticket Upgrade
          </h3>
          <button
            type="button"
            className="upgrade-modal__close"
            aria-label="Close"
            onClick={handleClose}
          >
            <IoIosClose />
          </button>
        </div>

        <div className="upgrade-modal__body">
          <div className="upgrade-modal__field">
            <label className="upgrade-modal__label">Current Ticket</label>
            <div className="upgrade-modal__readonly">{currentTicketName}</div>
          </div>

          <div className="upgrade-modal__field">
            <label className="upgrade-modal__label" htmlFor="upgrade-ticket">
              Upgrade Ticket
            </label>
            <select
              id="upgrade-ticket"
              className="upgrade-modal__select"
              value={upgradeTicketId}
              onChange={(e) => {
                setUpgradeTicketId(e.target.value);
                setIsVerified(false);
                setVerifyMessage("");
              }}
              disabled={isVerifying || isConfirming}
            >
              <option value="">Select upgrade ticket</option>
              {upgradeOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.ticket_name}
                  {t.ticket_amount ? ` — ${Number(t.ticket_amount).toFixed(2)}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="upgrade-modal__field">
            <label className="upgrade-modal__label" htmlFor="upgrade-count">
              Number of Tickets to Upgrade
            </label>
            <input
              id="upgrade-count"
              type="number"
              className="upgrade-modal__input"
              min={1}
              max={maxCount}
              value={upgradeCount}
              onChange={(e) => {
                setUpgradeCount(Number(e.target.value));
                setIsVerified(false);
                setVerifyMessage("");
              }}
              disabled={isVerifying || isConfirming}
            />
            <span className="upgrade-modal__hint">Max available: {maxCount}</span>
          </div>

          {/* {verifyDetails && <VerifyDetailsBlock details={verifyDetails} />} */}
          {isVerified && verifyMessage && (
            <div className="upgrade-modal__verified-msg">{verifyMessage}</div>
          )}
        </div>

        <div className="upgrade-modal__footer">
          <button
            type="button"
            className="upgrade-modal__btn upgrade-modal__btn--cancel"
            onClick={handleClose}
            disabled={isVerifying || isConfirming}
          >
            Cancel
          </button>
          {!isVerified ? (
            <button
              type="button"
              className="upgrade-modal__btn upgrade-modal__btn--verify"
              onClick={handleVerify}
              disabled={isVerifying}
            >
              {isVerifying ? "Verifying..." : "Verify"}
            </button>
          ) : (
            <button
              type="button"
              className="upgrade-modal__btn upgrade-modal__btn--confirm"
              onClick={handleConfirm}
              disabled={isConfirming}
            >
              {isConfirming ? "Confirming..." : "Confirm"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const ViewTransaction = (props: any) => {
  const { onClose, viewData, onRetry, eventTickets = [], onActionSuccess } = props;
  const [isSubmittingSms, setIsSubmittingSms] = useState(false);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [upgradeModalTicket, setUpgradeModalTicket] = useState<any>(null);
  const [cancelModalTicket, setCancelModalTicket] = useState<any>(null);
  const [resendingUpgradeId, setResendingUpgradeId] = useState<number | string | null>(null);

  const handleActionSuccess = () => {
    if (typeof onActionSuccess === "function") {
      onActionSuccess();
      return;
    }
    onClose();
  };

  const getCurrentTicketId = (participantTicket: any) => {
    if (participantTicket?.ticket_id) return participantTicket.ticket_id;
    const match = eventTickets.find(
      (t: EventTicket) => t.ticket_name === participantTicket.ticket_name
    );
    return match?.id;
  };

  const hasNoSeats = (ticket: any) => !ticket?.seats || ticket.seats.length === 0;

  const handleOpenCancelModal = (ticket: any) => {
    setCancelModalTicket(ticket);
  };

  const handleResendUpgradeConfirmation = async (upgrade: any) => {
    const upgradeRequestId = upgrade?.id;
    const upgradeTxnId = upgrade?.upgrade_onepay_transaction_id;
    if (!upgradeRequestId && !upgradeTxnId) {
      toast.error("Upgrade reference is missing.");
      return;
    }

    setResendingUpgradeId(upgradeRequestId || upgradeTxnId);
    try {
      const body: Record<string, unknown> = {};
      if (upgradeRequestId) body.upgrade_request_id = upgradeRequestId;
      if (upgradeTxnId) body.upgrade_onepay_transaction_id = upgradeTxnId;

      const response = await ticketUpgradeResendConfirmation(body);
      if (response?.data?.status === 200) {
        toast.success(response?.data?.message || "Upgrade confirmation email resent.");
      } else {
        toast.error(response?.data?.message || "Failed to resend upgrade confirmation email.");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to resend upgrade confirmation email."));
    } finally {
      setResendingUpgradeId(null);
    }
  };

  const handleRetry = async (type: "sms" | "email") => {
    if (!viewData?.onepay_transaction_id) {
      toast.error("Onepay Transaction is null. Please try again.");
      return;
    }

    if (type === "sms") setIsSubmittingSms(true);
    else setIsSubmittingEmail(true);

    try {
      const params = {
        onepay_transaction_id: viewData.onepay_transaction_id,
        is_send_sms: type === "sms",
        is_send_email: type === "email",
      };

      const response = await reSend(jsonToUrlParams(params));

      if (response?.data?.status === 200) {
        onRetry(viewData.transaction_reference || viewData.onepay_transaction_id);
        toast.success(response?.data?.message || "Retry successful!");
      } else {
        toast.error(response?.data?.message || "Retry failed.");
      }
    } catch (error) {
      toast.error("An error occurred during retry.");
    } finally {
      if (type === "sms") setIsSubmittingSms(false);
      else setIsSubmittingEmail(false);
    }
  };

  const totalTicketCount = viewData?.tickets?.reduce(
    (total: number, ticket: any) => total + Number(ticket.count || 0),
    0
  );
  const currency = viewData?.currency || "LKR";
  const formatMoney = (value: unknown) => {
    const num = toNumber(value);
    return `${currency} ${(num ?? 0).toFixed(2)}`;
  };
  const originalTicketAmount = toNumber(viewData?.gross_amount) ?? 0;
  const currentTicketValue =
    viewData?.tickets?.reduce(
      (total: number, ticket: any) =>
        total + Number(ticket.ticket_amount || 0) * Number(ticket.count || 0),
      0
    ) ?? 0;
  const discountedAmount = toNumber(viewData?.discounted_amount) ?? 0;
  const paidAmount = toNumber(viewData?.total_amount) ?? Math.max(0, originalTicketAmount - discountedAmount);
  const completedUpgrades = Array.isArray(viewData?.ticket_upgrades) ? viewData.ticket_upgrades : [];
  const hasUpgrades = completedUpgrades.length > 0;
  const upgradeChargesTotal = completedUpgrades.reduce(
    (total: number, upgrade: any) => total + Number(upgrade?.charges || 0),
    0
  );
  const upgradePayTotal = completedUpgrades.reduce(
    (total: number, upgrade: any) => total + Number(upgrade?.upgrade_pay_amount || 0),
    0
  );
  const upgradeTicketDifference = Math.max(0, upgradePayTotal - upgradeChargesTotal);
  const completedCancellations = Array.isArray(viewData?.ticket_cancellations)
    ? viewData.ticket_cancellations
    : [];
  const hasCancellations = completedCancellations.length > 0;
  const cancelledTicketCount = completedCancellations.reduce(
    (total: number, cancellation: any) => total + Number(cancellation?.cancel_count || 0),
    0
  );
  const cancelledTicketAmount = completedCancellations.reduce(
    (total: number, cancellation: any) => total + Number(cancellation?.total_ticket_amount || 0),
    0
  );
  const cancellationHandlingFees = completedCancellations.reduce(
    (total: number, cancellation: any) => total + Number(cancellation?.handling_fee || 0),
    0
  );
  const refundedTotal = completedCancellations.reduce(
    (total: number, cancellation: any) => total + Number(cancellation?.refund_amount || 0),
    0
  );
  const originalPurchasedCount = Number(totalTicketCount || 0) + cancelledTicketCount;
  const purchasePaid = Math.max(0, Number(originalTicketAmount) - Number(discountedAmount));
  const customerPaid = hasUpgrades
    ? Number(paidAmount)
    : purchasePaid;
  const netAfterRefund = Math.max(0, customerPaid - refundedTotal);
  const hasRefundIssued = refundedTotal > 0;
  const cancelOnlyCount = completedCancellations.filter(
    (cancellation: any) => cancellation?.cancellation_mode === "cancel_only"
  ).length;

  return (
    <OffCanvas heading="Event details">
      <Toaster position="top-right" reverseOrder={false} />
      <TicketUpgradeModal
        isOpen={!!upgradeModalTicket}
        ticket={upgradeModalTicket}
        eventTickets={eventTickets}
        viewData={viewData}
        onClose={() => setUpgradeModalTicket(null)}
        onSuccess={handleActionSuccess}
        getCurrentTicketId={getCurrentTicketId}
      />
      <TicketCancellationModal
        isOpen={!!cancelModalTicket}
        ticket={cancelModalTicket}
        viewData={viewData}
        onClose={() => setCancelModalTicket(null)}
        onSuccess={handleActionSuccess}
        getTicketId={getCurrentTicketId}
      />
      <div className="event-details-panel">
        <div className="event-details-panel__header">
          <h2 className="event-details-panel__title">View event details</h2>
          <button
            type="button"
            className="event-details-panel__close"
            aria-label="Close"
            onClick={onClose}
          >
            <IoIosClose />
          </button>
        </div>

        <div className="event-details-panel__body">
          <section className="detail-section">
            <h3 className="detail-section__title">User Details</h3>
            <DetailRow
              label="First Name"
              value={viewData?.customer_first_name || "Not Available"}
            />
            <DetailRow
              label="Last Name"
              value={viewData?.customer_last_name || "Not Available"}
            />
            <DetailRow
              label="Email Address"
              value={viewData?.customer_email || "Not Available"}
            />
            <DetailRow
              label="Phone Number"
              value={viewData?.customer_phone_no || "Not Available"}
            />
            <DetailRow
              label="Verification ID"
              value={`${viewData?.verification_id || "Not Available"} (${viewData?.verification_method || "N/A"})`}
            />
            <DetailRow
              label="Date and Time"
              value={viewData?.datetime || "Not Available"}
            />
          </section>

          <section className="detail-section">
            <h3 className="detail-section__title">Payment Details</h3>
            <DetailRow
              label="Status"
              value={
                <span className="status-badge status-badge--success">Success</span>
              }
            />
            <DetailRow
              label="Onepay Transaction ID"
              value={
                <span className="detail-row__mono">
                  {viewData?.onepay_transaction_id || "Not Available"}
                </span>
              }
            />
            <DetailRow
              label="Payment Method"
              value={viewData?.provider || "Not Available"}
            />
          </section>

          <section className="detail-section">
            <h3 className="detail-section__title">Notification details</h3>
            <div className="detail-row detail-row--action">
              <span className="detail-row__label">Email Notification</span>
              <div className="detail-row__action-group">
                <span
                  className={`status-badge ${
                    viewData?.is_email_send ? "status-badge--success" : "status-badge--danger"
                  }`}
                >
                  {viewData?.is_email_send ? "Success" : "Failed"}
                </span>
                <button
                  type="button"
                  className="resend-btn"
                  onClick={() => handleRetry("email")}
                  disabled={isSubmittingEmail}
                >
                  {isSubmittingEmail ? (
                    <span className="resend-btn__spinner" />
                  ) : (
                    <IoRefresh />
                  )}
                  Resend
                </button>
              </div>
            </div>
            <div className="detail-row detail-row--action">
              <span className="detail-row__label">SMS Notification</span>
              <div className="detail-row__action-group">
                <span
                  className={`status-badge ${
                    viewData?.is_sms_send ? "status-badge--success" : "status-badge--danger"
                  }`}
                >
                  {viewData?.is_sms_send ? "Success" : "Failed"}
                </span>
                <button
                  type="button"
                  className="resend-btn"
                  onClick={() => handleRetry("sms")}
                  disabled={isSubmittingSms}
                >
                  {isSubmittingSms ? (
                    <span className="resend-btn__spinner" />
                  ) : (
                    <IoRefresh />
                  )}
                  Resend
                </button>
              </div>
            </div>
          </section>

          <section className="detail-section">
            <h3 className="detail-section__title">Tickets Details</h3>
            {viewData?.tickets && viewData.tickets.length > 0 ? (
              <div className="ticket-table-wrap">
                <table className="ticket-table">
                  <thead>
                    <tr>
                      <th>Ticket Name</th>
                      <th>Ticket Price</th>
                      <th>No of Tickets</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewData.tickets.map((ticket: any, index: number) => (
                      <React.Fragment key={index}>
                        <tr>
                          <td
                            className="ticket-table__name"
                            style={{ color: TICKET_COLORS[index % TICKET_COLORS.length] }}
                          >
                            {index + 1}. {ticket.ticket_name}
                          </td>
                          <td>{Number(ticket.ticket_amount).toFixed(2)}</td>
                          <td className="ticket-table__count">{ticket.count}</td>
                        </tr>
                        <tr className="ticket-table__seats-row">
                          <td colSpan={3}>
                            <div className="ticket-actions">
                              {hasNoSeats(ticket) ? (
                                canUpgradeTicket(ticket, eventTickets) ? (
                                  <button
                                    type="button"
                                    className="ticket-upgrade__btn"
                                    onClick={() => setUpgradeModalTicket(ticket)}
                                  >
                                    Ticket Upgrade
                                  </button>
                                ) : null
                              ) : (
                                <div className="seat-tags">
                                  {ticket.seats.map((seat: string, i: number) => (
                                    <span key={i} className="seat-tag">
                                      {seat}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <button
                                type="button"
                                className="ticket-cancel__btn"
                                onClick={() => handleOpenCancelModal(ticket)}
                              >
                                Cancel Ticket
                              </button>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="ticket-empty">
                {Array.isArray(viewData?.ticket_cancellations) && viewData.ticket_cancellations.length > 0
                  ? "No active tickets remaining. See cancellation history below."
                  : "No tickets available."}
              </p>
            )}

            <div className="ticket-summary">
              <div className="ticket-summary__status">
                <span className="ticket-summary__pill ticket-summary__pill--active">
                  {totalTicketCount || 0} active
                </span>
                {hasCancellations && (
                  <span className="ticket-summary__pill ticket-summary__pill--cancelled">
                    {cancelledTicketCount} cancelled
                  </span>
                )}
                {hasUpgrades && (
                  <span className="ticket-summary__pill ticket-summary__pill--upgraded">
                    Upgraded
                  </span>
                )}
                <span className="ticket-summary__bought">
                  {hasCancellations || hasUpgrades
                    ? `${originalPurchasedCount} bought`
                    : "tickets"}
                </span>
              </div>

              <div className="ticket-summary__groups">
                <div className="ticket-summary__group">
                  <p className="ticket-summary__group-title">Purchase</p>
                  <div className="ticket-summary__row">
                    <span className="ticket-summary__label">Ticket value</span>
                    <span className="ticket-summary__value ticket-summary__value--sm">
                      {formatMoney(originalTicketAmount)}
                    </span>
                  </div>
                  {discountedAmount > 0 && (
                    <div className="ticket-summary__row">
                      <span className="ticket-summary__label">Discount</span>
                      <span className="ticket-summary__value ticket-summary__value--sm ticket-summary__value--discount">
                        − {formatMoney(discountedAmount)}
                      </span>
                    </div>
                  )}
                  <div className="ticket-summary__row">
                    <span className="ticket-summary__label">Paid at checkout</span>
                    <span className="ticket-summary__value ticket-summary__value--sm">
                      {formatMoney(purchasePaid)}
                    </span>
                  </div>
                </div>

                {hasUpgrades && (
                  <div className="ticket-summary__group">
                    <p className="ticket-summary__group-title">Upgrade</p>
                    <div className="ticket-summary__row">
                      <span className="ticket-summary__label">New ticket value</span>
                      <span className="ticket-summary__value ticket-summary__value--sm">
                        {formatMoney(currentTicketValue)}
                      </span>
                    </div>
                    {upgradeTicketDifference > 0 && (
                      <div className="ticket-summary__row">
                        <span className="ticket-summary__label">Price difference</span>
                        <span className="ticket-summary__value ticket-summary__value--sm">
                          + {formatMoney(upgradeTicketDifference)}
                        </span>
                      </div>
                    )}
                    {upgradeChargesTotal > 0 && (
                      <div className="ticket-summary__row">
                        <span className="ticket-summary__label">Upgrade charges</span>
                        <span className="ticket-summary__value ticket-summary__value--sm ticket-summary__value--charges">
                          + {formatMoney(upgradeChargesTotal)}
                        </span>
                      </div>
                    )}
                    <div className="ticket-summary__row">
                      <span className="ticket-summary__label">Extra paid for upgrade</span>
                      <span className="ticket-summary__value ticket-summary__value--sm ticket-summary__value--charges">
                        {formatMoney(upgradePayTotal)}
                      </span>
                    </div>
                  </div>
                )}

                {hasCancellations && (
                  <div className="ticket-summary__group ticket-summary__group--cancel">
                    <p className="ticket-summary__group-title">Cancellation</p>
                    <div className="ticket-summary__row">
                      <span className="ticket-summary__label">
                        Cancelled value ({cancelledTicketCount} ticket
                        {cancelledTicketCount === 1 ? "" : "s"})
                      </span>
                      <span className="ticket-summary__value ticket-summary__value--sm">
                        {formatMoney(cancelledTicketAmount)}
                      </span>
                    </div>
                    <div className="ticket-summary__row">
                      <span className="ticket-summary__label">Still active value</span>
                      <span className="ticket-summary__value ticket-summary__value--sm">
                        {formatMoney(currentTicketValue)}
                      </span>
                    </div>
                    <div
                      className={`ticket-summary__outcome${
                        hasRefundIssued
                          ? " ticket-summary__outcome--refund"
                          : " ticket-summary__outcome--none"
                      }`}
                    >
                      {hasRefundIssued ? (
                        <>
                          Refunded <strong>{formatMoney(refundedTotal)}</strong>
                          {cancellationHandlingFees > 0 && (
                            <span>
                              {" "}
                              · {formatMoney(cancellationHandlingFees)} handling fee kept
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          No money refunded
                          {cancelOnlyCount > 0 ? " (Cancel Only)" : ""}
                          {cancellationHandlingFees > 0 && (
                            <span>
                              {" "}
                              · handling fee {formatMoney(cancellationHandlingFees)}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                <div className="ticket-summary__footer">
                  <div className="ticket-summary__row ticket-summary__row--total">
                    <span className="ticket-summary__label">
                      {hasCancellations && hasRefundIssued
                        ? "Customer paid (net)"
                        : "Customer paid"}
                    </span>
                    <span className="ticket-summary__amount">
                      {formatMoney(
                        hasCancellations && hasRefundIssued ? netAfterRefund : customerPaid
                      )}
                    </span>
                  </div>
                  {hasUpgrades && !hasCancellations && (
                    <p className="ticket-summary__hint">
                      Includes checkout + upgrade payment
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>

          {hasUpgrades && (
            <section className="detail-section">
              <h3 className="detail-section__title">Ticket Upgrades</h3>
              <div className="ticket-upgrade-list">
                {completedUpgrades.map((upgrade: any, index: number) => {
                  const rowKey = upgrade?.id || upgrade?.upgrade_onepay_transaction_id || index;
                  const isResending = resendingUpgradeId === (upgrade?.id || upgrade?.upgrade_onepay_transaction_id);
                  const upgradePay = Number(upgrade?.upgrade_pay_amount || 0);
                  const upgradeCharges = Number(upgrade?.charges || 0);
                  const upgradeDiff = Math.max(0, upgradePay - upgradeCharges);
                  return (
                    <div key={rowKey} className="ticket-upgrade-card">
                      <div className="ticket-upgrade-card__main">
                        <div className="ticket-upgrade-card__title">
                          {upgrade?.from_ticket_name || "Ticket"} → {upgrade?.to_ticket_name || "Ticket"}
                        </div>
                        <div className="ticket-upgrade-card__meta">
                          Count: {upgrade?.upgrade_count || 0}
                          {upgrade?.upgraded_at && <> · {upgrade.upgraded_at}</>}
                        </div>
                        <div className="ticket-cancellation-card__amounts">
                          <span>Price difference: {formatMoney(upgradeDiff)}</span>
                          <span>Charges: {formatMoney(upgradeCharges)}</span>
                          <span className="ticket-upgrade-card__pay">
                            Upgrade paid: {formatMoney(upgradePay)}
                          </span>
                        </div>
                        {upgrade?.upgrade_onepay_transaction_id && (
                          <div className="ticket-upgrade-card__ref">
                            Ref: {upgrade.upgrade_onepay_transaction_id}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="resend-btn"
                        onClick={() => handleResendUpgradeConfirmation(upgrade)}
                        disabled={isResending}
                      >
                        {isResending ? <span className="resend-btn__spinner" /> : <IoRefresh />}
                        Resend confirmation
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {hasCancellations && (
            <section className="detail-section">
              <h3 className="detail-section__title">Ticket Cancellations</h3>
              <div className="ticket-upgrade-list">
                {completedCancellations.map((cancellation: any, index: number) => {
                  const isRefundMode = cancellation?.cancellation_mode === "cancel_and_refund";
                  const modeLabel = isRefundMode
                    ? "Cancel + Refund"
                    : cancellation?.cancellation_mode === "cancel_only"
                      ? "Cancel Only"
                      : cancellation?.cancellation_mode || "Cancellation";
                  const cancelCount = Number(cancellation?.cancel_count || 0);
                  const unitAmount = toNumber(cancellation?.unit_ticket_amount);
                  return (
                    <div
                      key={`${cancellation?.ticket_id || "cancel"}-${cancellation?.cancelled_at || index}`}
                      className="ticket-upgrade-card ticket-cancellation-card"
                    >
                      <div className="ticket-upgrade-card__main">
                        <div className="ticket-upgrade-card__title">
                          {cancellation?.ticket_name || "Ticket"}
                          <span className="status-badge status-badge--danger ticket-cancellation-card__badge">
                            {modeLabel}
                          </span>
                        </div>
                        <div className="ticket-upgrade-card__meta">
                          Cancelled {cancelCount} of {originalPurchasedCount} purchased
                          {unitAmount != null && (
                            <> · {formatMoney(unitAmount)} × {cancelCount}</>
                          )}
                          {cancellation?.cancelled_at && <> · {cancellation.cancelled_at}</>}
                        </div>
                        <div className="ticket-cancellation-card__amounts">
                          <span>Cancelled amount: {formatMoney(cancellation?.total_ticket_amount)}</span>
                          <span>Handling fee: {formatMoney(cancellation?.handling_fee)}</span>
                          <span className="ticket-cancellation-card__refund">
                            {isRefundMode
                              ? `Refunded: ${formatMoney(cancellation?.refund_amount)}`
                              : "No refund (Cancel Only)"}
                          </span>
                        </div>
                        {cancellation?.cancellation_note && (
                          <div className="ticket-upgrade-card__meta">
                            Note: {cancellation.cancellation_note}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>
    </OffCanvas>
  );
};

export default ViewTransaction;
